import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Hospital, X, Send, Mail, MessageSquare, Phone, Clock, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * PreAlertModal — modal para enviar pre-aviso al hospital destino.
 *
 * Props:
 *   service: objeto del servicio
 *   open: bool
 *   onClose: () => void
 *   onSent: () => void (para recargar el servicio y mostrar el prealert nuevo)
 */
export default function PreAlertModal({ service, open, onClose, onSent }) {
    const [contacts, setContacts] = useState([]);
    const [selected, setSelected] = useState({});
    const [eta, setEta] = useState(15);
    const [message, setMessage] = useState("");
    const [channels, setChannels] = useState({ email: true, sms: false, whatsapp: false });
    const [extraEmails, setExtraEmails] = useState("");
    const [extraPhones, setExtraPhones] = useState("");
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const { data } = await api.get("/hospital-contacts");
                setContacts(data || []);
                // Pre-seleccionar contactos del hospital del destino, si hay match por texto
                const dest = (service.destination_address || "").toLowerCase();
                const auto = {};
                (data || []).forEach((c) => {
                    if (dest && c.hospital_name && dest.includes(c.hospital_name.toLowerCase().split(" ")[0])) {
                        auto[c.id] = true;
                    }
                });
                setSelected(auto);
            } catch (e) { console.warn(e); }
        })();
    }, [open, service.destination_address]);

    if (!open) return null;

    const toggleContact = (id) => setSelected({ ...selected, [id]: !selected[id] });

    const submit = async () => {
        const contact_ids = Object.entries(selected).filter(([_, v]) => v).map(([k]) => k);
        const emails = extraEmails.split(/[,;\n\s]+/).filter(Boolean);
        const phones = extraPhones.split(/[,;\n\s]+/).filter(Boolean);
        const chs = Object.entries(channels).filter(([_, v]) => v).map(([k]) => k);

        if (contact_ids.length === 0 && emails.length === 0 && phones.length === 0) {
            toast.error("Selecciona al menos un contacto o agrega un destinatario manual");
            return;
        }
        if (chs.length === 0) {
            toast.error("Selecciona al menos un canal");
            return;
        }

        setSending(true);
        try {
            const { data } = await api.post(`/services/${service.id}/prealert`, {
                contact_ids,
                emails,
                phones,
                whatsapp_numbers: phones, // mismo input para WA
                channels: chs,
                eta_minutes: eta,
                custom_message: message || null,
            });
            const mock = data.mocked;
            toast[mock ? "warning" : "success"](mock
                ? "Pre-aviso registrado en modo simulado. Configura integraciones para envío real."
                : `Pre-aviso enviado a ${data.results.length} destinatario(s).`);
            onSent?.();
            onClose?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSending(false);
        }
    };

    // Agrupar contactos por hospital
    const grouped = {};
    contacts.forEach((c) => {
        const k = c.hospital_name || "—";
        if (!grouped[k]) grouped[k] = [];
        grouped[k].push(c);
    });

    return (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" onClick={onClose} data-testid="prealert-modal">
            <div className="max-w-3xl w-full bg-white rounded-sm overflow-hidden max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="bg-slate-900 text-white p-5 flex items-center justify-between border-b-4 border-red-600">
                    <div className="flex items-center gap-3">
                        <Hospital className="w-7 h-7 text-red-400" />
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-red-400 font-bold">PRE-AVISO HOSPITAL</div>
                            <h2 className="font-display font-bold text-xl">Notificar arribo de paciente</h2>
                            <div className="text-xs text-slate-300 mt-0.5 font-mono">Folio {service.folio} · {service.patient_name}</div>
                        </div>
                    </div>
                    <button onClick={onClose} aria-label="Cerrar" className="text-white hover:bg-slate-800 p-2 rounded-sm">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-auto p-5 space-y-5 bg-slate-50">
                    {/* Resumen del aviso */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="fm-card p-3 bg-white">
                            <label className="fm-label flex items-center gap-1"><Clock className="w-3 h-3" /> ETA (minutos)</label>
                            <input type="number" min="0" max="180" value={eta} onChange={(e) => setEta(parseInt(e.target.value) || 0)} className="fm-input font-mono text-lg font-bold" data-testid="eta-input" />
                        </div>
                        <div className="fm-card p-3 bg-white">
                            <label className="fm-label">Canales</label>
                            <div className="flex items-center gap-2 mt-2">
                                {[
                                    { key: "email", label: "Email", Icon: Mail },
                                    { key: "sms", label: "SMS", Icon: Phone },
                                    { key: "whatsapp", label: "WhatsApp", Icon: MessageSquare },
                                ].map(({ key, label, Icon }) => (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => setChannels({ ...channels, [key]: !channels[key] })}
                                        data-testid={`prealert-channel-${key}`}
                                        className={`px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors ${channels[key] ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500"}`}
                                    >
                                        <Icon className="w-3.5 h-3.5" /> {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Contactos guardados */}
                    <div className="fm-card p-3 bg-white">
                        <h3 className="font-display font-bold text-sm mb-2 flex items-center gap-2">
                            <Hospital className="w-4 h-4" /> Contactos registrados
                        </h3>
                        {contacts.length === 0 ? (
                            <div className="text-xs text-slate-400 py-3 text-center">
                                Sin contactos guardados todavía. Agrégalos en <code>/hospital-contacts</code>.
                            </div>
                        ) : (
                            <div className="space-y-2 max-h-48 overflow-auto">
                                {Object.entries(grouped).map(([hosp, list]) => (
                                    <div key={hosp} className="border border-slate-200 rounded-sm">
                                        <div className="px-3 py-1.5 bg-slate-50 text-[10px] uppercase tracking-widest font-bold text-slate-600">{hosp}</div>
                                        {list.map((c) => (
                                            <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer border-t border-slate-100 text-xs">
                                                <input
                                                    type="checkbox"
                                                    checked={!!selected[c.id]}
                                                    onChange={() => toggleContact(c.id)}
                                                    data-testid={`contact-check-${c.id}`}
                                                />
                                                <span className="font-medium">{c.name}</span>
                                                <span className="text-slate-400">· {c.role}</span>
                                                <span className="ml-auto text-[10px] text-slate-400 font-mono">
                                                    {c.email && <Mail className="w-3 h-3 inline" />}
                                                    {c.phone && <Phone className="w-3 h-3 inline ml-1" />}
                                                    {c.whatsapp && <MessageSquare className="w-3 h-3 inline ml-1" />}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Extras manuales */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="fm-label">Emails adicionales (separados por coma)</label>
                            <textarea
                                value={extraEmails}
                                onChange={(e) => setExtraEmails(e.target.value)}
                                placeholder="urgencias@hospital.com, jefe@hospital.com"
                                className="fm-input text-xs"
                                rows={2}
                                data-testid="extra-emails-input"
                            />
                        </div>
                        <div>
                            <label className="fm-label">Teléfonos adicionales (SMS / WhatsApp)</label>
                            <textarea
                                value={extraPhones}
                                onChange={(e) => setExtraPhones(e.target.value)}
                                placeholder="+5215512345678, +5215587654321"
                                className="fm-input text-xs"
                                rows={2}
                                data-testid="extra-phones-input"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="fm-label">Mensaje adicional (opcional)</label>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Paciente requiere cama de UCI · Equipo de trauma preparado · etc."
                            className="fm-input"
                            rows={3}
                            data-testid="custom-message-input"
                        />
                    </div>

                    <div className="fm-card p-3 bg-blue-50 border-l-4 border-l-blue-500 text-xs flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                            El pre-aviso enviará automáticamente: <b>datos del paciente</b>, <b>signos vitales más recientes</b>,
                            <b> Glasgow/dolor/triage</b>, <b>antecedentes/alergias</b>, <b>tratamiento administrado</b>, <b>ETA</b>, <b>unidad y paramédico responsable</b>.
                        </div>
                    </div>
                </div>

                <div className="border-t border-slate-200 p-4 flex items-center justify-end gap-3 bg-white">
                    <button onClick={onClose} className="fm-btn-outline">Cancelar</button>
                    <button onClick={submit} disabled={sending} className="fm-btn-accent" data-testid="send-prealert-btn">
                        <Send className="w-3.5 h-3.5" /> {sending ? "Enviando..." : "Enviar pre-aviso"}
                    </button>
                </div>
            </div>
        </div>
    );
}
